import React, { useState, useEffect, useMemo, useRef } from 'react';
import VariantsStep1 from './VariantsStep1';
import VariantsStep2 from './VariantsStep2';

/**
 * Variants tab: 2-step flow.
 * Step 1: Define attribute names (e.g. Color, Size), then "Create Variants" or "Product Has no Variants".
 * Step 2: Fill in variant values, SKUs, images (existing variants grid).
 */
const VariantsForm = ({
  formData,
  handleChange,
  isLoading,
  variantsDraftData,
  setVariantsDraftData,
  onValidationChange,
  onNoVariants,
  onInternalStepChange
}) => {
  const hasDraft = useMemo(() => {
    const d = variantsDraftData;
    const hasLevel1 = d?.level1Values && Array.isArray(d.level1Values) && d.level1Values.length > 0;
    const hasVariants = d?.variants && Array.isArray(d.variants) && d.variants.length > 0;
    return !!(d?.variantType || hasLevel1 || hasVariants);
  }, [variantsDraftData]);

  const [step, setStep] = useState(() => (hasDraft ? 2 : 1));
  const [variantTypeFromStep1, setVariantTypeFromStep1] = useState(() =>
    variantsDraftData?.variantType || ''
  );

  const hadDraftRef = useRef(hasDraft);

  useEffect(() => {
    if (typeof onInternalStepChange === 'function') {
      onInternalStepChange(step);
    }
  }, [step, onInternalStepChange]);

  useEffect(() => {
    if (hasDraft && !hadDraftRef.current) {
      hadDraftRef.current = true;
      setStep(2);
      if (variantsDraftData?.variantType) {
        setVariantTypeFromStep1(variantsDraftData.variantType);
      }
    } else if (!hasDraft) {
      hadDraftRef.current = false;
    }
  }, [hasDraft, variantsDraftData?.variantType]);

  const variantType = variantTypeFromStep1 || variantsDraftData?.variantType || 'Color/Size/Pack';

  const buildDraftFromStep1 = (attributeNames, variantValuesByAttr) => {
    const typeStr = attributeNames.join('/').trim() || 'Color/Size/Pack';
    const levels = typeStr.split('/').map((s) => s.trim()).filter(Boolean);
    const l1 = (variantValuesByAttr[0] && variantValuesByAttr[0].length > 0) ? variantValuesByAttr[0] : [''];
    const l2 = variantValuesByAttr[1] || [];
    const l3 = variantValuesByAttr[2] || [];
    const emptyLevel3Fields = [{ value: '', sku: '', images: [] }];
    let variants;
    if (levels.length === 1) {
      variants = l1.map(() => [{ level2: '', level3Fields: emptyLevel3Fields, sku: '', images: {} }]);
    } else if (levels.length === 2) {
      variants = l1.map(() =>
        (l2.length > 0 ? l2 : ['']).map((l2Val) => ({
          level2: l2Val,
          level3Fields: emptyLevel3Fields,
          sku: '',
          images: {}
        }))
      );
          } else {
      variants = l1.map(() =>
        (l2.length > 0 ? l2 : ['']).map((l2Val) => ({
          level2: l2Val,
          level3Fields: (l3.length > 0 ? l3 : ['']).map((v) => ({ value: v, sku: '', images: [] })),
          sku: '',
          images: {}
        }))
      );
    }
    return { variantType: typeStr, level1Values: l1, variants };
  };

  const handleStep1Continue = (attributeNames, variantValuesByAttr) => {
    const typeStr = attributeNames.join('/').trim() || 'Color/Size/Pack';
    setVariantTypeFromStep1(typeStr);
    if (typeof setVariantsDraftData === 'function' && Array.isArray(variantValuesByAttr)) {
      const draft = buildDraftFromStep1(attributeNames, variantValuesByAttr);
      setVariantsDraftData(draft);
    }
    setStep(2);
  };

  const handleNoVariants = () => {
    if (typeof onNoVariants === 'function') {
      onNoVariants();
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const initialAttributes = useMemo(() => {
    const d = variantsDraftData;
    if (d?.variantType && typeof d.variantType === 'string') {
      return d.variantType.split('/').map((s) => s.trim());
    }
    return [];
  }, [variantsDraftData]);

  const initialVariantValues = useMemo(() => {
    const d = variantsDraftData;
    if (d?.level1Values && Array.isArray(d.level1Values) && d?.variants && Array.isArray(d.variants)) {
      const levels = (d.variantType || '').split('/').map((s) => s.trim()).filter(Boolean);
      const l1 = d.level1Values;
      const l2 = levels.length >= 2 ? (d.variants[0] || []).map((v) => v.level2).filter(Boolean) : [];
      const l3 = levels.length >= 3 && (d.variants[0] || [])[0]?.level3Fields
        ? (d.variants[0][0].level3Fields || []).map((f) => f.value).filter(Boolean)
        : [];
      return [l1, l2, l3].slice(0, Math.max(1, levels.length));
    }
    return [];
  }, [variantsDraftData]);

  if (step === 1) {
    return (
      <VariantsStep1
        onContinue={handleStep1Continue}
        onNoVariants={handleNoVariants}
        initialAttributes={initialAttributes}
        initialVariantValues={initialVariantValues}
        isLoading={isLoading}
        filteredEnums={formData?.productTypeFilteredEnums}
        attributeEnums={formData?.productTypeAttributeEnums ?? {}}
      />
    );
  }

  return (
    <VariantsStep2
      formData={formData}
      handleChange={handleChange}
      isLoading={isLoading}
      variantsDraftData={variantsDraftData}
      setVariantsDraftData={setVariantsDraftData}
      onValidationChange={onValidationChange}
      variantType={variantType}
      onBackToStep1={handleBackToStep1}
    />
  );
};

export default VariantsForm;
